import { useState, useEffect } from 'react';

interface ExchangeRates {
  [key: string]: number;
}

export default function CurrencyConverter() {
  const [amount, setAmount] = useState<string>('60');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('TZS');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({});
  const [convertedAmount, setConvertedAmount] = useState<number>(0);

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
    { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R' }
  ];

  useEffect(() => {
    // Simulate exchange rates (in a real app, you'd fetch from an API)
    const rates: ExchangeRates = {
      USD: 1,
      EUR: 0.85,
      GBP: 0.73,
      TZS: 2580,
      KES: 129,
      ZAR: 18.5
    };
    setExchangeRates(rates);
  }, []);

  useEffect(() => {
    if (amount && exchangeRates[fromCurrency] && exchangeRates[toCurrency]) {
      const numAmount = parseFloat(amount);
      if (!isNaN(numAmount)) {
        // Convert to USD first, then to target currency
        const usdAmount = numAmount / exchangeRates[fromCurrency];
        const converted = usdAmount * exchangeRates[toCurrency];
        setConvertedAmount(converted);
      }
    }
  }, [amount, fromCurrency, toCurrency, exchangeRates]);

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const formatAmount = (value: number, currencyCode: string): string => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currencyCode === 'TZS' || currencyCode === 'KES') {
      return `${currency?.symbol}${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    }
    return `${currency?.symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <i className="ri-money-dollar-circle-line text-green-500"></i>
        Currency Converter
      </h3>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="currency-amount" className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
          <input
            type="number"
            id="currency-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A67C52] focus:border-[#A67C52] outline-none transition-all text-sm"
            placeholder="Enter amount"
            aria-label="Amount to convert"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="currency-from" className="block text-sm font-medium text-gray-700 mb-2">From</label>
            <select
              id="currency-from"
              value={fromCurrency}
              onChange={(e) => setFromCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A67C52] focus:border-[#A67C52] outline-none transition-all text-sm cursor-pointer"
              aria-label="Convert from currency"
            >
              {currencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="currency-to" className="block text-sm font-medium text-gray-700 mb-2">To</label>
            <select
              id="currency-to"
              value={toCurrency}
              onChange={(e) => setToCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A67C52] focus:border-[#A67C52] outline-none transition-all text-sm cursor-pointer"
              aria-label="Convert to currency"
            >
              {currencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex justify-center">
          <button
            onClick={swapCurrencies}
            className="p-2 text-gray-500 hover:text-[#A67C52] transition-colors cursor-pointer"
            aria-label="Swap currencies - exchange from and to currencies"
          >
            <i className="ri-swap-line text-xl"></i>
          </button>
        </div>
        
        <div className="bg-[#F8F4F0] p-4 rounded-lg border border-[#A67C52]/20">
          <p className="text-sm text-gray-600 mb-1">Converted Amount</p>
          <p className="text-2xl font-bold text-[#A67C52]" aria-live="polite" aria-atomic="true">
            {formatAmount(convertedAmount, toCurrency)}
          </p>
        </div>
        
        <p className="text-xs text-gray-400 text-center">
          * Rates are approximate and for reference only
        </p>
      </div>
    </div>
  );
}
